import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import _, { set } from 'lodash';
// Components
import BreadCrumb from '../../components/BreadCrumb';
import InputGroup from '../../components/FormElements/InputGroup';
import Button from '../../components/Buttons';
import DirectInputContent from './DirectInputContent';
import MergeWinesModal from './MergeWinesModal';
import ProducerModal from './ProducerModal';
import { useExternalInputContext } from './ExternalInputContext';
import { SwalConfirmation } from '../../components/confirmation-popup/swalConfirmation';

// Hooks and Context
import { useDirectInputApi } from '../../store/api/hooks/directInputApiHook';
import { useExternalInputApi } from '../../store/api/hooks/externalInputApiHook';
import { useNavigationWarning, navigationWarningManager } from '../../hooks/useNavigationWarning';
import { URLS } from '../../_config';
import { useWineContext } from './useWineContext';
import LoaderSpinner from '../../components/loaderspinner';

// Utility to split text into clickable words
const splitTextIntoWords = (text: string) => {
  if (!text) return [];
  // Split by spaces and remove empty strings or just punctuation
  return text.split(/\s+/).filter(word => word && /\w/.test(word));
};

// Component to render clickable words
const ClickableWords: React.FC<{ text: string; onWordClick: (word: string) => void }> = ({ text, onWordClick }) => {
  const words = splitTextIntoWords(text);
  
  return (
    <div className="px-2">
      {words.length > 0 ? (
        words.map((word, index) => (
          <React.Fragment key={`${word}-${index}`}>
            <span 
              className="cursor-pointer hover:text-blue-600"
              onClick={() => onWordClick(word)}
            >
              {word}
            </span>
            {index < words.length - 1 && ' '}
          </React.Fragment>
        ))
      ) : text || ''}
    </div>
  );
};

const ExternalInput: React.FC = () => {

  const navigate = useNavigate();
  const { uploadFile, checkMatch, acceptMatch, undoMatch } = useExternalInputApi();
  
  // Use the External Input Context
  const {
    // Search and Filter State
    externalKeywordsList,
    setExternalKeywordsList,
    externalFilterId, 
    setExternalFilterId,
    externalLockedProducerLiterals,
    setExternalLockedProducerLiterals,
    externalSearchPartial,
    setExternalSearchPartial,
    externalSearchPartialProducer,
    setExternalSearchPartialProducer,
    
    // Table Data State 
    externalFetchedWines,
    setExternalFetchedWines,
    externalFetchedProducers,
    setExternalFetchedProducers,
    externalSelectedWineRows,
    setExternalSelectedWineRows,
    externalMergeModalWines,
    setExternalMergeModalWines,
    
    // Pagination State
    externalWinesPage,
    setExternalWinesPage,
    externalProducersPage,
    setExternalProducersPage,
    externalHasMoreWines,
    setExternalHasMoreWines,
    externalHasMoreProducers,
    setExternalHasMoreProducers, 
    externalTotalRowsOfWines,
    setExternalTotalRowsOfWines,
    externalTotalRowsOfProducers,
    setExternalTotalRowsOfProducers,
    
    // UI State
    externalShowMergeModal,
    setExternalShowMergeModal,
    externalShowProducerModal,
    setExternalShowProducerModal,
    externalSelectedProducerId,
    setExternalSelectedProducerId,

    // External Input File Data State
    externalNumberOfFiles,
    setExternalNumberOfFiles,
    externalUniqueKey,
    setExternalUniqueKey,
    externalFileName,
    setExternalFileName,
    externalTableData,
    setExternalTableData,
    externalCurrentIndex,
    setExternalCurrentIndex,
    externalDataLoaded,
    setExternalDataLoaded,
    externalMatchedWine,
    setExternalMatchedWine
  } = useExternalInputContext();
  
  // Aliases for simpler usage in component
  const keywordsList = externalKeywordsList;
  const setKeywordsList = setExternalKeywordsList;
  const filter_id = externalFilterId;
  const setfilter_id = setExternalFilterId;
  const lockedProducerLiterals = externalLockedProducerLiterals;
  const setLockedProducerLiterals = setExternalLockedProducerLiterals;
  const search_partial = externalSearchPartial;
  const setSearch_partial = setExternalSearchPartial;
  const search_partialProducer = externalSearchPartialProducer;
  const setSearch_partialProducer = setExternalSearchPartialProducer;
  
  // Use stored values from context instead of local state
  const fetchedWines = externalFetchedWines;
  const setFetchedWines = setExternalFetchedWines;
  const fetchedProducers = externalFetchedProducers;
  const setFetchedProducers = setExternalFetchedProducers;
  const selectedWineRows = externalSelectedWineRows;
  const setSelectedWineRows = setExternalSelectedWineRows;
  const mergeModalWines = externalMergeModalWines;
  const setMergeModalWines = setExternalMergeModalWines;
  
  // Pagination State
  const winesPage = externalWinesPage;
  const setWinesPage = setExternalWinesPage;
  const producersPage = externalProducersPage;
  const setProducersPage = setExternalProducersPage;
  const hasMoreWines = externalHasMoreWines;
  const setHasMoreWines = setExternalHasMoreWines;
  const hasMoreProducers = externalHasMoreProducers;
  const setHasMoreProducers = setExternalHasMoreProducers;
  const totalRowsOfWines = externalTotalRowsOfWines;
  const setTotalRowsOfWines = setExternalTotalRowsOfWines;
  const totalRowsOfProducers = externalTotalRowsOfProducers;
  const setTotalRowsOfProducers = setExternalTotalRowsOfProducers;
  
  // UI State
  const showMergeModal = externalShowMergeModal;
  const setShowMergeModal = setExternalShowMergeModal;
  const showProducerModal = externalShowProducerModal;
  const setShowProducerModal = setExternalShowProducerModal;
  const selectedProducerId = externalSelectedProducerId;
  const setSelectedProducerId = setExternalSelectedProducerId;

  // External Input File Data State
  const fileName = externalFileName;
  const setFileName = setExternalFileName;
  const tableData = externalTableData;
  const setTableData = setExternalTableData;
  const currentIndex = externalCurrentIndex;
  const setCurrentIndex = setExternalCurrentIndex;
  const dataLoaded = externalDataLoaded;
  const setDataLoaded = setExternalDataLoaded;
  const matchedWine = externalMatchedWine;
  const setMatchedWine = setExternalMatchedWine;
  const [undoEnabled, setUndoEnabled] = useState(false);
  const uniqueKey = externalUniqueKey;
  const setUniqueKey = setExternalUniqueKey;
  const numberOfFilesArray = externalNumberOfFiles;
  const setNumberOfFilesArray = setExternalNumberOfFiles;
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isCheckingMatch, setIsCheckingMatch] = useState<boolean>(false);
  // Separate loading states for Next and Back navigation
  const [isNextLoading, setIsNextLoading] = useState<boolean>(false);
  const [isBackLoading, setIsBackLoading] = useState<boolean>(false);
  const [checkedMatchId, setCheckedMatchId] = useState<string | null>(null);
  const [checkMatchDescription, setCheckMatchDescription] = useState<string | null>(null);

  // Navigation warning protection
  const hasActiveWork = dataLoaded || tableData.length > 0 || isUploading || isCheckingMatch;
  
  // Register/unregister this page for navigation protection
  useEffect(() => {
    const pageId = 'external-input';
    navigationWarningManager.registerPage(pageId, hasActiveWork);

    return () => {
      navigationWarningManager.unregisterPage(pageId);
    };
  }, [hasActiveWork]);

  // Use the navigation warning hook
  const { navigateWithWarning } = useNavigationWarning({
    hasUnsavedWork: hasActiveWork
  });

  // State for selected literal in wine table
  const [selectedLiteral, setSelectedLiteral] = useState<{ id: string; literal: string } | null>(null);
  // Handler for clicking the literal cell
  const handleLiteralClick = (id: string, literal: string) => {
    setSelectedLiteral({ id, literal });
  };

  // Handler for Accept Match button
  const handleAcceptMatch = async () => {
    if (!selectedLiteral || !tableData || tableData.length === 0) return;
    // Find the selected wine row from fetchedWines
    const selectedWine = fetchedWines.find(wine => String(wine.id) === String(selectedLiteral.id));
    // Get current page wine data
    const currentWine = tableData[0];
    if (!currentWine) return;
    // Prepare vintage value by removing the 2nd character
    let vintage = currentWine.vintage;
    if (typeof vintage === 'string' && vintage.length > 1) {
      vintage = vintage.slice(0, 1) + vintage.slice(2);
    }
    // Prepare payload
    const payload = {
      wine_db_id: parseInt(selectedWine.id, 10),
      wine_literal: selectedWine.literal + String(vintage),
      mongo_id: currentWine.literal,
      wine_n_id: currentWine.wine_vinid,
      vintage: currentWine.vintage
    };
    try {
      setIsCheckingMatch(true);
      const response = await acceptMatch(payload);
      toast.success(response?.data?.message || 'Match accepted successfully');
      // Clear selected literal after accepting match
      setSelectedLiteral(null);
      
      // Automatically navigate to next wine after successful match
      if (currentIndex < numberOfFilesArray - 1) {
        await handleNextWine();
      } else {
        toast.success('You have reached the last wine in the file');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to accept match');
    } finally {
      setIsCheckingMatch(false);
    }
  }

    const handleUndoMatch = async () => {
    // Show confirmation dialog before proceeding
    SwalConfirmation.triggerSwal(
      'Undo Match Confirmation',
      'Are you sure you want to undo this wine match?',
      async () => {
        // Get wine_id from matchedWine (check match response)
        // Get mongo_id from current page's external data
        const wine_id = parseInt(checkedMatchId ?? '', 10);
        const mongo_id = tableData && tableData.length > 0 ? tableData[0]?.literal : undefined;
        // console
        // if (!wine_id || !mongo_id) {
        //   toast.error('Cannot undo match: missing wine_id or mongo_id');
        //   return;
        // }
        try {
          const response = await undoMatch({ wine_id,mongo_id });
          toast.success(response?.data?.message || 'Undo match successful');
          // Clear selected literal after undoing match
          setSelectedLiteral(null);
          // Optionally refresh match state here
          checkMatchForCurrentLiteral();
        } catch (error: any) {
          toast.error(error?.message || 'Failed to undo match');
        }
      }
    );
  };

  // Handler for Reset Match button
  const handleResetMatch = () => {
    setSelectedLiteral(null);
  };

  // Function to check match for current literal
  const checkMatchForCurrentLiteral = async () => {
    if (tableData && tableData.length > 0 && tableData[0]?.literal) {
      try {
        setIsCheckingMatch(true);
        const response = await checkMatch({ mongo_id: tableData[0].literal });
        const matchData = response?.data?.data || null;
        // Populate matchedWine with the literal field from matchData
        setMatchedWine(matchData?.literal ?? '');
        setCheckedMatchId(matchData?.id ?? null);
        setCheckMatchDescription(matchData?.description ?? null);
        setUndoEnabled(!!(matchData && matchData.id));
      } catch (error: any) {
        console.error('Error checking match:', error);
        toast.error(error?.message || 'Failed to check match');
        setMatchedWine(null);
        setUndoEnabled(false);
      } finally {
        setIsCheckingMatch(false);
      }
    }
  };

  //upload file
  const handleFileUpload = async () => {
    if (!file) {
      toast.error("Please select a file to upload.");
      return;
    }
    try {
      setIsUploading(true);
      // Load only first page with per_page=1
      const res = await uploadFile({ file, page: 1, per_page: 1 });
      console.log("File upload response:", res);
      const result = res?.data?.data?.data || [];
      
      // Extract and store the unique key for subsequent requests
      const extractedUniqueKey = res?.data?.data?.unique_key || "";
      setUniqueKey(extractedUniqueKey);
      
      setNumberOfFilesArray(res?.data?.data?.total_count || 0);
      setTableData(result);
      setCurrentIndex(0); // Reset to first item when new data is loaded
      setDataLoaded(result.length > 0);

      // Reset DirectInputContent search params
      setKeywordsList([]);
      setfilter_id('');
      setLockedProducerLiterals([]);
      setSearch_partial(true);
      setSearch_partialProducer(true);
      setFetchedWines([]);
      setFetchedProducers([]);
      // Clear selected literal when new file is uploaded
      setSelectedLiteral(null);
  
      if (result.length > 0) {
        console.log("Table data set:", result);
        toast.success(res?.data?.message || "Data loaded successfully.");
        // Check match for the first item is automatically handled by useEffect
      } else {
        toast.error("No data found in the uploaded file.");
      }
    } catch (error: any) {
      setTableData([]);
      setCurrentIndex(0);
      setMatchedWine(null);
      setDataLoaded(false);
      setUniqueKey("");
      toast.error(error);
    } finally {
        setIsUploading(false);
    }
  };


  // Update the handleFileChange function to save the filename to context:
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setFileName(selectedFile.name); // Save the filename to context
    }
  };

  const { getWineById, getProducerById, getWines, getProducer } = useDirectInputApi();

  // Refs for preventing duplicate calls and debounce cleanup
  const isInitialMount = useRef(true);
  const isFetchingRef = useRef(false);
  const debouncedFetchRef = useRef<any>(null);

  // Local UI State
  const [newNarrowKeyword, setNewNarrowKeyword] = useState('');

  // Derived counts for badges
  const winesBadgeCount = fetchedWines.length === 0 ? 0 : totalRowsOfWines;
  const producersBadgeCount = fetchedProducers.length === 0 ? 0 : totalRowsOfProducers;

  // Loading State
  const [isLoadingWines, setIsLoadingWines] = useState(false);
  const [isLoadingProducers, setIsLoadingProducers] = useState(false);
  const [isLoadingMoreWines, setIsLoadingMoreWines] = useState(false);
  const [isLoadingMoreProducers, setIsLoadingMoreProducers] = useState(false);

  // API Functions
  const fetchWineById = async (ids: string[]): Promise<any[]> => {
    try {
      const wineDetails = await Promise.all(ids.map(id => getWineById(id)));
      return wineDetails.map(res => res?.data?.data || {});
    } catch (err) {
      console.error('Failed to fetch wine details:', err);
      return [];
    }
  };

  const fetchWines = async (
    keywords: string[] = keywordsList,
    fieldId: string = filter_id,
    producers: string[] = lockedProducerLiterals,
    page: number = 1,
    append: boolean = false
  ): Promise<void> => {
    try {
      if (append) {
        setIsLoadingMoreWines(true);
      } else {
        setIsLoadingWines(true);
      }
      
      const per_page = 40;
      const params: any = {
        keywords: keywords.join(','),
        filter_id: fieldId || '',
        producers: producers.join(','),
        search_partial: search_partial,
        page,
        per_page
      };
      
      const data = await getWines(params);
      setTotalRowsOfWines(data?.data?.data?.total);
      const result = Array.isArray(data) ? data : (data?.data?.data?.result || []);
      
      if (append) {
        setFetchedWines(prev => [...prev, ...result]);
      } else {
        setFetchedWines(result);
        setWinesPage(1);
      }
      
      setHasMoreWines(result.length === per_page);
      
    } catch (error) {
      console.error('Failed to fetch wines:', error);
      setHasMoreWines(false);
    } finally {
      if (append) {
        setIsLoadingMoreWines(false);
      } else {
        setIsLoadingWines(false);
      }
    }
  };

  const fetchProducers = async (
    keywords: string[] = keywordsList,
    fieldId: string = filter_id,
    producers: string[] = lockedProducerLiterals,
    page: number = 1,
    append: boolean = false
  ): Promise<void> => {
    try {
      if (append) {
        setIsLoadingMoreProducers(true);
      } else {
        setIsLoadingProducers(true);
      }
      
      const per_page = 40;
      const params: any = {
        keywords: keywords.join(','),
        filter_id: fieldId || '',
        producers: producers.join(','),
        search_partial: search_partialProducer,
        page,
        per_page
      };
      
      const data = await getProducer(params);
      setTotalRowsOfProducers(data?.data?.data?.total);
      const result = Array.isArray(data) ? data : (data?.data?.data?.result || []);
      
      if (append) {
        setFetchedProducers(prev => [...prev, ...result]);
      } else {
        setFetchedProducers(result);
        setProducersPage(1);
      }
      
      setHasMoreProducers(result.length === per_page);
      
    } catch (error) {
      console.error('Failed to fetch producers:', error);
      setHasMoreProducers(false);
    } finally {
      if (append) {
        setIsLoadingMoreProducers(false);
      } else {
        setIsLoadingProducers(false);
      }
    }
  };

  const fetchBothTablesData = useCallback(async (
    keywords: string[] = keywordsList,
    fieldID: string = filter_id,
    producers: string[] = lockedProducerLiterals,
    page: number = 1,
    append: boolean = false
  ) => {
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      await Promise.all([
        fetchWines(keywords, fieldID, producers, page, append),
        fetchProducers(keywords, fieldID, producers, page, append)
      ]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      isFetchingRef.current = false;
    }
  }, [search_partial, search_partialProducer]);

  // Handlers
  const createDebouncedFetch = useCallback(() => {
    if (debouncedFetchRef.current) {
      debouncedFetchRef.current.cancel();
    }
    debouncedFetchRef.current = _.debounce((id: string, keywords: string[], producers: string[]) => {
      if (keywords.length > 0 || id.trim() || producers.length > 0) {
        fetchBothTablesData(keywords, id, producers, 1, false);
      }
    }, 750);
    return debouncedFetchRef.current;
  }, [fetchBothTablesData]);

  const handleKeywordKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && newNarrowKeyword.trim()) {
      e.preventDefault();
      const newKeyword = newNarrowKeyword.trim();
      if (keywordsList.includes(newKeyword)) {
          setNewNarrowKeyword('');
          return; // Do not update state or trigger API if duplicate
      }
      setKeywordsList((prev: string[]) => Array.from(new Set([...prev, newKeyword])));
      setNewNarrowKeyword('');
    }
  };

  const removeKeyword = (index: number): void => {
    setKeywordsList(prev => {
      const updated = prev.filter((_, i) => i !== index);
      // If all keywords are cleared, also clear selected producers
      if (updated.length === 0) {
        setLockedProducerLiterals([]);
      }
      return updated;
    });
  };

  const handlefilter_idChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setfilter_id(value);
    if (debouncedFetchRef.current) {
      debouncedFetchRef.current.cancel();
    }
    if (!value.trim() && keywordsList.length === 0 && lockedProducerLiterals.length === 0) {
      clearDataIfNoSearchCriteria();
      return;
    }
    if (value.trim() || keywordsList.length > 0 || lockedProducerLiterals.length > 0) {
      const debouncedFetch = createDebouncedFetch();
      debouncedFetch(value, keywordsList, lockedProducerLiterals);
    }
  };

  const handleWordClick = (word: string): void => {
    if (word && !keywordsList.includes(word)) {
      // Clean up the word by removing punctuation at the beginning/end
      const cleanWord = word.replace(/^[^\w]+|[^\w]+$/g, '');
      if (word) {
              if (keywordsList.includes(word)) {
          return; // Do not update state or trigger API if duplicate
      }
      setKeywordsList((prev: string[]) => Array.from(new Set([...prev, word])));
      }
    }
  };

  const handleProducerIdClick = (id: string): void => {
    if (id && !lockedProducerLiterals.includes(id)) {
      setLockedProducerLiterals(prev => [...prev, id]);
    }
  };

  const handleWineRowSelect = (row: any, checked: boolean): void => {
    setSelectedWineRows(prev => {
      if (checked) {
        if (prev.some(r => r.id === row.id)) return prev;
        if (prev.length >= 3) {
          toast.error('You can select at most 3 wines for merging.');
          return prev;
        }
        return [...prev, row];
      } else {
        return prev.filter(r => r.id !== row.id);
      }
    });
  };

  const handleMergeSelected = async (): Promise<void> => {
    if (selectedWineRows.length < 1 || selectedWineRows.length > 3) return;
    const ids = selectedWineRows.map(row => row.id);
    const winesData = await fetchWineById(ids);
    setMergeModalWines(winesData);
    setShowMergeModal(true);
  };

  const handleMergeSuccess = (): void => {
    // Clear selected wine rows
    setSelectedWineRows([]);
    setShowMergeModal(false);
    // Refresh the wines table data
    if (keywordsList.length > 0 || filter_id.trim() || lockedProducerLiterals.length > 0) {
      fetchBothTablesData(keywordsList, filter_id, lockedProducerLiterals, 1, false);
    }
  };

  const handleEditProducer = (id: string): void => {
    setSelectedProducerId(id);
    setShowProducerModal(true);
  };

  // Update handleAddWineToProducer to pass fromExternalInput=true
  const handleAddWineToProducer = (producerId: string): void => {
    // Get current wine data
    const currentWine = tableData[0];
    
    // Navigate with both URL parameters and state
    navigate(
      URLS.WINE_ADDITION.ADD_WINE_TO_PRODUCER.replace(':id', producerId) + 
      `?fromExternalInput=true&dataLoaded=${dataLoaded}`, 
      {
        state: {
          externalWineData: currentWine,
          fromExternalInput: true
        }
      }
    );
  };

  // Handler for "Add Wine New Producer" button
  const handleAddWineNewProducer = (): void => {
    if (!dataLoaded || !tableData || tableData.length === 0) {
      toast.error("No wine data available to add");
      return;
    }
    
    // Get current wine data
    const currentWine = tableData[0];
    
    // Navigate to new producer page with external input data
    navigate(URLS.WINE_ADDITION.NEW_PRODUCER + `?fromExternalInput=true&dataLoaded=${dataLoaded}`, {
      state: {
        externalWineData: currentWine,
        fromExternalInput: true
      }
    });
  };

  const handleProducerUpdated = (): void => {
    setShowProducerModal(false);
    fetchBothTablesData(keywordsList, filter_id, lockedProducerLiterals, producersPage, false);
  };

  const removeLockProducerLiteral = (index: number): void => {
    setLockedProducerLiterals(prev => prev.filter((_, i) => i !== index));
  };

  const clearDataIfNoSearchCriteria = (): void => {
    setFetchedWines([]);
    setFetchedProducers([]);
    setSelectedWineRows([]);
  };

  // Navigation handlers for the wine array - using API pagination with unique key
  const handleNextWine = async () => {
    if (currentIndex < numberOfFilesArray - 1 && !isNextLoading) {
      setIsNextLoading(true);
      // Calculate next page based on currentIndex + 1 since we want sequential pages
      const nextPage = currentIndex + 1; // Changed from currentIndex + 2
      
      try {
        if (!uniqueKey) {
          toast.error("File session expired. Please reload the file.");
          return;
        }
        
        const res = await uploadFile({ 
          unique_key: uniqueKey, 
          page: nextPage + 1, // Add 1 since API expects 1-based index
          per_page: 1
        });
        
        const result = res?.data?.data?.data || [];
        
        if (result && result.length > 0) {
          // Update both local state and context
          setTableData(result);
          setExternalTableData(result);
          
          setCurrentIndex(nextPage); // Directly set to nextPage instead of increment
          setExternalCurrentIndex(nextPage);
          
          setMatchedWine(null);
          setUndoEnabled(false);
          // Clear selected literal when navigating
          setSelectedLiteral(null);

          // Reset DirectInputContent state
          setFetchedWines([]);
          setFetchedProducers([]);
          setKeywordsList([]);
          setfilter_id('');
          setLockedProducerLiterals([]);
          
          // Show actual page number (1-based for display)
          toast.success(`Loaded wine ${nextPage + 1} of ${numberOfFilesArray}`);
        } else {
          toast.error("Failed to load the next wine");
        }
      } catch (error: any) {
        console.error('Error loading next wine:', error);
        toast.error(error?.message || "Failed to load next wine");
      } finally {
        setIsNextLoading(false);
      }
    }
  };

  const handlePreviousWine = async () => {
    if (currentIndex > 0 && !isBackLoading) {
      setIsBackLoading(true);
      const prevPage = currentIndex; // Page numbers are 1-based, currentIndex is 0-based
      try {
        if (!uniqueKey) {
          toast.error("File session expired. Please reload the file.");
          return;
        }
        
        console.log(`Fetching wine page ${prevPage} with unique key`);
        // Use unique key instead of file for subsequent requests
        const res = await uploadFile({ 
          unique_key: uniqueKey, 
          page: prevPage, 
          per_page: 1
        });
        
        const result = res?.data?.data?.data || [];
        
        if (result && result.length > 0) {
          console.log("Loaded previous wine:", result);
          setTableData(result); // This should contain just one wine object
          setCurrentIndex(prev => prev - 1);
          setMatchedWine(null); // Reset current match before moving to previous item
          setUndoEnabled(false);
          // Clear selected literal when navigating
          setSelectedLiteral(null);

          // Reset DirectInputContent
          setFetchedWines([]);
          setFetchedProducers([]);
          setKeywordsList([]);
          setfilter_id('');
          setLockedProducerLiterals([]);
          
          toast.success(`Loaded wine ${prevPage} of ${numberOfFilesArray}`);
        } else {
          console.error("No wine data returned for previous page");
          toast.error("Failed to load the previous wine");
        }
      } catch (error: any) {
        console.error('Error fetching previous wine:', error);
        toast.error(error?.message || 'Failed to fetch previous wine');
      } finally {
        setIsBackLoading(false);
      }
    } else if (isBackLoading) {
      toast("Please wait, navigation in progress...");
    } else {
      toast.error("This is the first wine in the list");
    }
  };

  // Effects

  // Effect to handle file and uniqueKey management
  useEffect(() => {
    if (file) {
      // We can't store the File object directly, but we can keep a reference to it
      // in the component state as long as the component is mounted
      console.log("File object saved in component state");
    } else if (fileName && !uniqueKey) {
      console.log("File name found in context but uniqueKey is missing:", fileName);
      // If we have a filename but no uniqueKey, we might be in a state where navigation won't work
      // The user might need to reload the file
    } else if (uniqueKey) {
      console.log("Using uniqueKey for pagination:", uniqueKey);
    }
  }, [file, fileName, uniqueKey]);

  useEffect(() => {
    if (isFetchingRef.current) return;

    setSelectedWineRows([]);

    const hasSearchCriteria = keywordsList.length > 0 || filter_id.trim() || lockedProducerLiterals.length > 0;

    if (!hasSearchCriteria) {
      clearDataIfNoSearchCriteria();
      return;
    }

    fetchBothTablesData(keywordsList, filter_id, lockedProducerLiterals, 1, false);
  }, [keywordsList, lockedProducerLiterals, search_partial, search_partialProducer, fetchBothTablesData]);

  useEffect(() => {
    setSelectedWineRows([]);
    if (filter_id.trim() && (keywordsList.length > 0 || lockedProducerLiterals.length > 0)) {
      const debouncedFetch = createDebouncedFetch();
      debouncedFetch(filter_id, keywordsList, lockedProducerLiterals);
    }
  }, [filter_id]);

  // Check for matches when table data is loaded or currentIndex changes
  useEffect(() => {
    if (tableData && tableData.length > 0) {
      checkMatchForCurrentLiteral();
    }
  }, [currentIndex, tableData]);

  useEffect(() => {
    return () => {
      if (debouncedFetchRef.current) {
        debouncedFetchRef.current.cancel();
      }
    };
  }, []);

  return (
    <div className="main-content-box" id='externalInputPage'>
      <BreadCrumb title='Wine Addition - External Input' />
      <div className="border rounded-lg p-4">
        <div className="space-y-4">
          <div className="grid grid-cols-12 gap-4">
            <div className="fileInput col-span-6">
              <InputGroup
                type="filetype"
                label="File"
                name="file"
                handleChange={handleFileChange}
                value={fileName}
              />
            </div>
            <div className="col-span-2">
              <Button variant="dark" size="md" type="button" onClick={handleFileUpload} className='loadButton min-w-24'>
                <div className="flex items-center justify-center" >
                {isUploading ? (
                  <LoaderSpinner size={16} />
                ) : (
                  <span>Load</span>
                )}
              </div>
              </Button>
            </div>
            <div className="col-span-1 col-start-12">
                <div className="flex flex-col items-end text-sm">
                  {dataLoaded ? `${currentIndex + 1} of ${numberOfFilesArray}` : '0 of 0'}
                </div>
                {dataLoaded && !uniqueKey && (
                  <div className="text-sm text-red-500 mt-1">
                    Session expired. Please reload file.
                  </div>
                )}
            </div>
          </div>
        </div>

        <hr className="my-3"/>

        <table className="w-full border shadow-sm">
          <tbody className="divide-y divide-gray-200">
            <tr className="hover:bg-gray-50">
              <th className="py-3 px-2 w-1/6 text-left text-lg font-semibold text-gray-800 border-r">Mongo ID</th>
              <td className="py-3 px-2 text-sm">
                <div className="">{tableData && tableData.length > 0 ? tableData[0]?.literal || '' : ''}</div>
              </td>
            </tr>
            <tr className="hover:bg-gray-50">
              <th className="py-3 px-2 text-left text-lg font-semibold text-gray-800 border-r">Label Name</th>
              <td className="py-3 text-sm">
                <ClickableWords 
                  text={tableData && tableData.length > 0 ? tableData[0]?.label || '' : ''} 
                  onWordClick={handleWordClick}
                />
              </td>
            </tr>
            <tr className="hover:bg-gray-50">
              <th className="py-3 px-2 w-1/6 text-left text-lg font-semibold text-gray-800 border-r">Producer Name</th>
              <td className="py-3 w-2/6 text-sm border-r">
                <ClickableWords 
                  text={tableData && tableData.length > 0 ? tableData[0]?.producer_name || '' : ''} 
                  onWordClick={handleWordClick}
                />
              </td>
              <th className="py-3 px-2 w-1/6 text-left text-lg font-semibold text-gray-800 border-l border-r">Producer Show</th>
              <td className="py-3 w-2/6text-sm">
                <ClickableWords 
                  text={tableData && tableData.length > 0 ? tableData[0]?.producer_name_show || '' : ''} 
                  onWordClick={handleWordClick}
                />
              </td>
            </tr>
            <tr className="hover:bg-gray-50">
              <td className="border-r" colSpan={4}>
                <div className="grid grid-cols-6">
                  <div className="space-y-1 border-r last:border-none">
                    <div className="text-base font-semibold text-gray-800 p-2">Country</div>
                    <div className="text-sm">
                      <ClickableWords 
                        text={tableData && tableData.length > 0 ? tableData[0]?.country || '' : ''} 
                        onWordClick={handleWordClick}
                      />
                    </div>
                  </div>
                  <div className="space-y-1 border-r last:border-none">
                    <div className="text-base font-semibold text-gray-800 p-2">Region Code</div>
                    <div className=" px-2 text-sm">{tableData && tableData.length > 0 ? tableData[0]?.region_code || '' : ''}</div>
                  </div>
                  <div className="space-y-1 border-r last:border-none">
                    <div className="text-base font-semibold text-gray-800 p-2">Region</div>
                    <div className="text-sm">
                      <ClickableWords 
                        text={tableData && tableData.length > 0 ? tableData[0]?.region || '' : ''} 
                        onWordClick={handleWordClick}
                      />
                    </div>
                  </div>
                  <div className="space-y-1 border-r last:border-none">
                    <div className="text-base font-semibold text-gray-800 p-2">Sub-Region</div>
                    <div className="text-sm">
                      <ClickableWords 
                        text={tableData && tableData.length > 0 ? tableData[0]?.location || '' : ''} 
                        onWordClick={handleWordClick}
                      />
                    </div>
                  </div>
                  <div className="space-y-1 border-r last:border-none">
                    <div className="text-base font-semibold text-gray-800 p-2">Appellation</div>
                    <div className="text-sm">
                      <ClickableWords 
                        text={tableData && tableData.length > 0 ? tableData[0]?.locale || '' : ''} 
                        onWordClick={handleWordClick}
                      />
                    </div>
                  </div>
                  <div className="space-y-1 border-r last:border-none">
                    <div className="text-base font-semibold text-gray-800 p-2">Sub-Appellation</div>
                    <div className="text-sm">
                      <ClickableWords 
                        text={tableData && tableData.length > 0 ? tableData[0]?.site || '' : ''} 
                        onWordClick={handleWordClick}
                      />
                    </div>
                  </div>
                </div>
              </td>
            </tr>
            <tr className="hover:bg-gray-50">
              <td className="border-r" colSpan={4}>
                <div className="grid grid-cols-6">
                  <div className="space-y-1 border-r last:border-none">
                    <div className="text-base font-semibold text-gray-800 p-2">Wine Vin ID</div>
                    <div className="px-2 text-sm break-all overflow-hidden">{tableData && tableData.length > 0 ? tableData[0]?.wine_vinid || '' : ''}</div>
                  </div>
                  <div className="space-y-1 border-r last:border-none">
                    <div className="text-base font-semibold text-gray-800 p-2">Vintage</div>
                    <div className=" px-2 text-sm">{tableData && tableData.length > 0 ? tableData[0]?.vintage || '' : ''}</div>
                  </div>
                  <div className="space-y-1 border-r last:border-none">
                    <div className="text-base font-semibold text-gray-800 p-2">Variety</div>
                    <div className="  px-2 text-sm">{tableData && tableData.length > 0 ? tableData[0]?.variety || '' : ''}</div>
                  </div>
                  <div className="space-y-1 border-r last:border-none">
                    <div className="text-base font-semibold text-gray-800 p-2">Dryness</div>
                    <div className="  px-2 text-sm">{tableData && tableData.length > 0 ? tableData[0]?.sweetness || '' : ''}</div>
                  </div>
                  <div className="space-y-1 border-r last:border-none">
                    <div className="text-base font-semibold text-gray-800 p-2">Color Class</div>
                    <div className="  px-2 text-sm">{tableData && tableData.length > 0 ? tableData[0]?.color || '' : ''}</div>
                  </div>
                  <div className="space-y-1 border-r last:border-none">
                    <div className="text-base font-semibold text-gray-800 p-2">Type</div>
                    <div className="  px-2 text-sm">{tableData && tableData.length > 0 ? tableData[0]?.wine_type || '' : ''}</div>
                  </div>
                </div>
              </td>
            </tr>
            <tr className="hover:bg-gray-50">
              <th className="py-3 px-2 text-left text-base font-semibold text-gray-800 border-r">Matched Wine</th>
              <td className="py-3 px-2 text-sm" colSpan={3}>
                <div className="">
                  {isCheckingMatch ? "Checking match..." : 
                    matchedWine && checkMatchDescription ? `${matchedWine} - ${checkMatchDescription}` :
                    matchedWine || checkMatchDescription || ""
                  }
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="flex justify-center flex-wrap gap-3 my-3">
        <button
          className={`px-6 py-2 border rounded-md transition-colors ${
            !dataLoaded ? "border-gray-300 text-gray-300 cursor-not-allowed"
            :"border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
          }`}
          disabled={!dataLoaded}
          onClick={handleAddWineNewProducer}
          id='externalHover'
        >
          Add Wine New Producer
        </button>
          <button 
            className={`min-w-24 px-6 py-2 border rounded-md transition-colors ${
              !dataLoaded || currentIndex === 0 || isBackLoading
              ? "border-gray-300 text-gray-300 cursor-not-allowed" 
              : "border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
            }`}
            onClick={handlePreviousWine}
            disabled={!dataLoaded || currentIndex === 0 || isBackLoading}
            id='externalHover'
          >
            <div className="flex items-center justify-center" style={{ minHeight: 24 }}>
              {isBackLoading ? <LoaderSpinner size={16} /> : <span>Back</span>}
            </div>
          </button>
          <button 
            className={` min-w-48 px-6 py-2 border rounded-md transition-colors ${
              !dataLoaded || currentIndex >= numberOfFilesArray - 1 || isNextLoading
              ? "border-gray-300 text-gray-300 cursor-not-allowed" 
              : "border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
            }`}
            onClick={handleNextWine}
            disabled={!dataLoaded || currentIndex >= numberOfFilesArray - 1 || isNextLoading}
            id='externalHover'
          >
            <div className="flex items-center justify-center" style={{ minHeight: 24 }}>
              {isNextLoading ? <LoaderSpinner size={16} /> : <span>Next(without match)</span>}
            </div>
          </button>
          {/* Accept Match and Reset Match buttons enabled only when a literal is selected */}
          <button
            className={`px-6 py-2 border rounded-md transition-colors ${
              selectedLiteral && dataLoaded && !matchedWine
                ? "border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                : "border-gray-300 text-gray-300 cursor-not-allowed"
            }`}
            disabled={!selectedLiteral || !dataLoaded || !!matchedWine}
            onClick={handleAcceptMatch}
            id='externalHover'
          >
            Accept Match
          </button>
          <button
            className={`px-6 py-2 border rounded-md transition-colors ${
              selectedLiteral
                ? "border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                : "border-gray-300 text-gray-300 cursor-not-allowed"
            }`}
            disabled={!selectedLiteral}
            onClick={handleResetMatch}
            id='externalHover'
          >
            Reset Match
          </button>
          <button 
            className={`px-6 py-2 border rounded-md transition-colors ${undoEnabled ? "border-red-600 text-red-600 hover:bg-red-600 hover:text-white" : "border-gray-300 text-gray-300 cursor-not-allowed"}`}
            disabled={!undoEnabled}
            onClick={handleUndoMatch}
            id='externalHover'
          >
            Undo Match
          </button>
        </div>

        <DirectInputContent 
          newNarrowKeyword={newNarrowKeyword}
          setNewNarrowKeyword={setNewNarrowKeyword}
          filter_id={filter_id}
          handlefilter_idChange={handlefilter_idChange}
          search_partialProducer={search_partialProducer}
          setSearch_partialProducer={setSearch_partialProducer}
          search_partial={search_partial}
          setSearch_partial={setSearch_partial}
          keywordsList={keywordsList}
          removeKeyword={removeKeyword}
          lockedProducerLiterals={lockedProducerLiterals}
          removeLockProducerLiteral={removeLockProducerLiteral}
          fetchedProducers={fetchedProducers}
          hasMoreProducers={hasMoreProducers}
          isLoadingProducers={isLoadingProducers}
          isLoadingMoreProducers={isLoadingMoreProducers}
          handleEditProducer={handleEditProducer}
          handleAddWineToProducer={handleAddWineToProducer}
          handleProducerIdClick={handleProducerIdClick}
          handleWordClick={handleWordClick}
          onLoadMoreProducers={() => {
            if (!isLoadingMoreProducers && hasMoreProducers) {
              const nextPage = producersPage + 1;
              setProducersPage(nextPage);
              fetchProducers(keywordsList, filter_id, lockedProducerLiterals, nextPage, true);
            }
          }}
          producersBadgeCount={producersBadgeCount}
          fetchedWines={fetchedWines}
          hasMoreWines={hasMoreWines}
          isLoadingWines={isLoadingWines}
          isLoadingMoreWines={isLoadingMoreWines}
          handleWineRowSelect={handleWineRowSelect}
          selectedWineRows={selectedWineRows}
          onLoadMoreWines={() => {
            if (!isLoadingMoreWines && hasMoreWines) {
              const nextPage = winesPage + 1;
              setWinesPage(nextPage);
              fetchWines(keywordsList, filter_id, lockedProducerLiterals, nextPage, true);
            }
          }}
          winesBadgeCount={winesBadgeCount}
          handleMergeSelected={handleMergeSelected}
          shouldShowEditWines={true} // Enable wine editing in external input
          handleKeywordKeyPress={handleKeywordKeyPress}
          fromExternalInput={true}
          dataLoaded={dataLoaded}
          selectedLiteral={selectedLiteral}
          handleLiteralClick={handleLiteralClick}
        />

        <div className="border rounded-lg mt-6">
          <div className="bg-gray-50 py-2 px-2 border-b">
            <h3 className="font-medium text-gray-900">External Resources</h3>
          </div>
          <table className="w-full">
            <tbody className="divide-y divide-gray-200">
              <tr className="hover:bg-gray-50">
                <th className="py-3 px-2 w-1/6 text-left text-sm font-normal text-gray-900">RP Note</th>
                <td className="py-3 px-2">
                  <div className="text-sm ">
                    {tableData && tableData.length > 0 && tableData[0]?.literal ? (
                      <a 
                        href={`https://wsf.robertparker.com/wines/${tableData[0].literal}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 no-underline"
                      >
                        {`https://wsf.robertparker.com/wines/${tableData[0].literal}`}
                      </a>
                    ) : ''}
                  </div>
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <th className="py-3 px-2 w-1/6 text-left text-sm font-normal text-gray-900">Appellation Lookup</th>
                <td className="py-3 px-2">
                  <div className="text-sm">
                    {tableData && tableData.length > 0 ? (
                      [
                        tableData[0].country,
                        tableData[0].region,
                        tableData[0].location,
                        tableData[0].locale
                      ]
                        .filter(Boolean)
                        .join(', ') || ''
                    ) : ''}
                  </div>
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <th className="py-3 px-2 w-1/6 text-left text-sm font-normal text-gray-900">Variety Lookup</th>
                <td className="py-3 px-2">
                  <div className="text-sm">
                    {tableData && tableData.length > 0 ? tableData[0]?.variety || '' : ''}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {/* Modals */}
      {showMergeModal && (
        <MergeWinesModal
          isOpen={showMergeModal}
          onClose={() => setShowMergeModal(false)}
          selectedWines={mergeModalWines}
          onMergeSuccess={handleMergeSuccess}
        />
      )}

      {showProducerModal && selectedProducerId && (
        <ProducerModal
          isOpen={showProducerModal}
          onClose={() => setShowProducerModal(false)}
          producerId={selectedProducerId}
          onUpdated={handleProducerUpdated}
        />
      )}
    </div>
  );
};

export default ExternalInput;